import type { Task } from "@app/core";
import { useEffect, useRef } from "react";

import { useReducedMotion } from "../../lib/motion/index.js";
import { useCalendarFocusStore } from "../../stores/index.js";

/**
 * When the calendar-focus store targets this task (e.g. from a search "View"
 * click), scroll its chip into view and return `flash: true` so the renderer can
 * apply the fading highlight ring. Exact virtual ids identify one occurrence;
 * source ids intentionally match every rendered occurrence of that series.
 */
export function useFlashHighlight(task: Task) {
  const highlightId = useCalendarFocusStore((s) => s.highlightId);
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const flash = matchesFlashHighlight(task, highlightId);

  useEffect(() => {
    if (!flash || !ref.current) return;
    const el = ref.current;
    // Defer so it runs after TimeAxisGrid's own mount scroll-to-current-hour
    // (parent effects run after child effects in the same commit).
    const id = setTimeout(() => el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" }), 50);
    return () => clearTimeout(id);
  }, [flash, highlightId, reduce, task.id]);

  return { ref, flash };
}

/** Exact virtual ids target one occurrence; a source id remains a supported
 * fallback for callers that intentionally refer to a whole series. */
export function matchesFlashHighlight(task: Task, highlightId: string | null): boolean {
  if (!highlightId) return false;
  if (task.id === highlightId) return true;
  return !!task.virtual && !!task.parent_id && task.parent_id === highlightId;
}
